import {
  DATA_SOURCE_UI_COLUMN_WINDOW_MAX_LIMIT,
  DATA_SOURCE_UI_WINDOW_MAX_LIMIT,
  type DataSourceCellsWindowRequest,
  type DataSourceWindowRequest,
} from '@blocksuite/data-view';
import {
  DATABASE_LARGE_DATA_POLICY,
  FILE_INDEX_COLUMN_IDS,
  FILE_INDEX_MANAGED_BY,
} from '@streamlain/database-identity';

import type { DatabaseBlockDataSource } from './data-source.js';
import {
  type DatabaseRowShardDocLoader,
  type DatabaseRowShardSnapshotBarrier,
  type RawValueJsonConverter,
  type RowShardCell,
} from './data-source-contracts.js';

export const INITIAL_DATABASE_ROW_WINDOW_LIMIT =
  DATABASE_LARGE_DATA_POLICY.ui.defaultPageSize;

export const FILE_INDEX_ROOT_SCHEMA_HARD_LIMIT_BYTES = 2 * 1024 * 1024;

export const FILE_INDEX_VIEW_HARD_LIMIT = 32;

export const FILE_INDEX_SHARD_WRITE_OVERHEAD_BYTES =
  DATABASE_LARGE_DATA_POLICY.shard.writeAccountingOverheadBytes;

export function encodedJsonBytes(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).byteLength;
}

export const rowShardDocLoaders = new WeakMap<
  object,
  Set<DatabaseRowShardDocLoader>
>();

export const rowShardSnapshotBarriers = new WeakMap<
  object,
  Set<DatabaseRowShardSnapshotBarrier>
>();

export function isFileIndexSystemColumnId(
  columnId: string | undefined
): boolean {
  return (
    columnId === 'title' ||
    FILE_INDEX_COLUMN_IDS.includes(
      columnId as (typeof FILE_INDEX_COLUMN_IDS)[number]
    )
  );
}

export function isFileIndexManagedColumnData(
  data: Record<string, unknown> | undefined
): boolean {
  return (
    data?.managedBy === FILE_INDEX_MANAGED_BY || data?.systemManaged === true
  );
}

export function disposeUnknown(disposable: unknown): void {
  if (!disposable) return;
  if (typeof disposable === 'function') {
    disposable();
    return;
  }
  const disposableObject = disposable as {
    dispose?: unknown;
    unsubscribe?: unknown;
  };
  if (typeof disposableObject.dispose === 'function') {
    disposableObject.dispose();
    return;
  }
  if (typeof disposableObject.unsubscribe === 'function') {
    disposableObject.unsubscribe();
  }
}

export function normalizeDatabaseRowWindowRequest(
  request: DataSourceWindowRequest
): {
  offset: number;
  limit: number;
} {
  const offset = request.offset ?? 0;
  const limit = request.limit;
  if (!Number.isInteger(offset) || offset < 0) {
    throw new RangeError(`Window offset must be a non-negative integer`);
  }
  if (!Number.isInteger(limit) || limit <= 0) {
    throw new RangeError(`Window limit must be a positive integer`);
  }
  if (limit > DATA_SOURCE_UI_WINDOW_MAX_LIMIT) {
    throw new RangeError(
      `Window limit must be <= ${DATA_SOURCE_UI_WINDOW_MAX_LIMIT}; use an explicit bulk API for larger reads`
    );
  }
  return { offset, limit };
}

export function normalizeDatabasePropertyWindowRequest(
  request: DataSourceCellsWindowRequest,
  propertyIds: readonly string[]
): {
  propertyOffset: number;
  propertyLimit: number;
  propertyIds: string[];
} {
  const propertyOffset = request.propertyOffset ?? 0;
  const propertyLimit = request.propertyLimit ?? propertyIds.length;
  if (!Number.isInteger(propertyOffset) || propertyOffset < 0) {
    throw new RangeError(
      `Property window offset must be a non-negative integer`
    );
  }
  if (propertyIds.length === 0 && propertyLimit === 0) {
    return {
      propertyOffset,
      propertyLimit,
      propertyIds: [],
    };
  }
  if (!Number.isInteger(propertyLimit) || propertyLimit <= 0) {
    throw new RangeError(`Property window limit must be a positive integer`);
  }
  if (propertyLimit > DATA_SOURCE_UI_COLUMN_WINDOW_MAX_LIMIT) {
    throw new RangeError(
      `Property window limit must be <= ${DATA_SOURCE_UI_COLUMN_WINDOW_MAX_LIMIT}`
    );
  }
  return {
    propertyOffset,
    propertyLimit,
    propertyIds: propertyIds.slice(
      propertyOffset,
      propertyOffset + propertyLimit
    ),
  };
}

export const SCHEMA_LOCKED_INVALID_CELL_VALUE = Symbol(
  'schemaLockedInvalidCellValue'
);

export function isSystemManagedPropertyData(data: unknown): boolean {
  return (
    data != null &&
    typeof data === 'object' &&
    !Array.isArray(data) &&
    (data as { systemManaged?: unknown }).systemManaged === true
  );
}

export function uint8ArraysEqual(left: Uint8Array, right: Uint8Array): boolean {
  if (left.byteLength !== right.byteLength) return false;
  for (let index = 0; index < left.byteLength; index++) {
    if (left[index] !== right[index]) return false;
  }
  return true;
}

export function projectionSearchCellText(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export function isReadonlyYMap(value: unknown): value is {
  delete: (key: string) => void;
  forEach: (callback: (value: unknown, key: string) => void) => void;
  get: (key: string) => unknown;
  has: (key: string) => boolean;
  set: (key: string, value: unknown) => void;
} {
  return (
    Boolean(value) &&
    typeof value === 'object' &&
    typeof (
      value as {
        delete?: unknown;
        forEach?: unknown;
        get?: unknown;
        has?: unknown;
        set?: unknown;
      }
    ).forEach === 'function' &&
    typeof (value as { get?: unknown }).get === 'function' &&
    typeof (value as { set?: unknown }).set === 'function' &&
    typeof (value as { delete?: unknown }).delete === 'function'
  );
}

export function normalizeRowShardCell(
  columnId: string,
  rawCell: unknown
): RowShardCell {
  if (
    rawCell &&
    typeof rawCell === 'object' &&
    !Array.isArray(rawCell) &&
    'columnId' in rawCell &&
    'value' in rawCell
  ) {
    const cell = rawCell as { columnId: string; value: unknown };
    return { columnId: cell.columnId, value: cell.value };
  }
  return { columnId, value: rawCell };
}

export function fromJsonValue(
  meta: RawValueJsonConverter,
  input: {
    data: Record<string, unknown>;
    dataSource: DatabaseBlockDataSource;
    value: unknown;
  }
): unknown {
  const fromJson = meta.config.rawValue.fromJson;
  const schema = meta.config.jsonValue.schema;
  if (!fromJson || !schema) {
    return undefined;
  }
  const result = schema.safeParse(input.value);
  if (!result.success) {
    return undefined;
  }
  return fromJson({
    data: input.data,
    dataSource: input.dataSource,
    value: result.data,
  });
}
